import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  GraduationCap, 
  Trash2, 
  TreeDeciduous, 
  Users,
  Shield,
  ArrowRight,
  Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const services = [
  {
    id: 'cleaning',
    icon: Sparkles,
    title: 'Commercial Cleaning',
    shortDescription: 'Contract cleaning solutions for offices, healthcare, fitness, and retail environments.',
    fullDescription: 'Systematic, high-standard cleaning programmes designed for commercial environments. We deliver measurable consistency through documented processes and quality-controlled operations.',
    features: [
      'Office & corporate cleaning',
      'Healthcare facility cleaning',
      'Gym & leisure centre cleaning',
      'Retail & showroom maintenance',
      'Scheduled & periodic deep cleaning',
      'Compliance-ready sanitisation',
    ],
  },
  {
    id: 'staffing',
    icon: Users,
    title: 'Hospitality Staffing',
    shortDescription: 'Trained, vetted hospitality personnel for hotels, restaurants, and short-term lets.',
    fullDescription: 'Professional staffing solutions with structured deployment, accountability, and performance management. We provide dependable workforce coverage for hospitality operations of any scale.',
    features: [
      'Housekeeping teams',
      'Kitchen porters',
      'General porters',
      'Front-of-house support',
      'Event hospitality teams',
      'Short-term let turnovers',
    ],
  },
  {
    id: 'turnovers',
    icon: GraduationCap,
    title: 'Property Turnovers',
    shortDescription: 'High-volume property turnovers with guaranteed deadline delivery.',
    fullDescription: 'Scalable turnover operations for student accommodation, BTR, and short-term rental portfolios. We manage complex schedules with precision and documented quality assurance.',
    features: [
      'Full unit restoration',
      'Kitchen & bathroom deep cleaning',
      'End of tenancy standards',
      'Inventory & damage reporting',
      'Linen coordination',
      'Communal area maintenance',
    ],
  },
  {
    id: 'security',
    icon: Shield,
    title: 'Security & Front-of-House',
    shortDescription: 'Professional security and reception personnel for commercial premises.',
    fullDescription: 'Trained, SIA-licensed security personnel and professional front-of-house staff. We provide structured coverage with clear accountability and incident management protocols.',
    features: [
      'Manned security services',
      'Reception & concierge staffing',
      'Access control management',
      'Visitor management systems',
      'Event security & stewarding',
      'Night patrol services',
    ],
  },
  {
    id: 'waste',
    icon: Trash2,
    title: 'Waste & Hygiene Management',
    shortDescription: 'Compliant waste handling and washroom hygiene services.',
    fullDescription: 'Comprehensive waste management and hygiene services that ensure regulatory compliance and operational efficiency across your facility portfolio.',
    features: [
      'Commercial waste management',
      'Recycling programmes',
      'Clinical waste disposal',
      'Washroom servicing',
      'Consumables management',
    ],
  },
  {
    id: 'grounds',
    icon: TreeDeciduous,
    title: 'Grounds & External Maintenance',
    shortDescription: 'Professional grounds and external maintenance for commercial portfolios.',
    fullDescription: 'Scheduled grounds maintenance services that maintain professional standards across your external environments, car parks, and communal outdoor spaces.',
    features: [
      'Landscaping programmes',
      'Litter management',
      'Winter gritting services',
      'Pressure washing',
      'Car park maintenance',
    ],
  },
];

const ServiceCard = ({ service, isExpanded, onHover, onLeave, onTap }) => {
  return (
    <motion.div
      layout
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onTap}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -8 }}
      className={`service-card relative bg-white border rounded-sm overflow-hidden transition-all duration-500 cursor-pointer ${
        isExpanded 
          ? 'border-accent shadow-2xl shadow-accent/20' 
          : 'border-slate-200 shadow-lg hover:shadow-xl'
      }`}
      data-testid={`service-card-${service.id}`}
    >
      {/* Animated gradient border effect */}
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
        style={{ background: 'linear-gradient(90deg, transparent, rgba(255, 94, 0, 0.05), transparent)' }}
      />
      
      <motion.div layout className="relative p-6 md:p-8 z-10">
        <motion.div layout className="flex items-start gap-4">
          <motion.div 
            className={`p-3 rounded-sm transition-all duration-300 ${isExpanded ? 'bg-accent' : 'bg-primary-light'}`}
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <service.icon className={`w-6 h-6 transition-colors duration-300 ${isExpanded ? 'text-white' : 'text-primary'}`} />
          </motion.div>
          <div className="flex-1">
            <motion.h3
              layout
              className={`font-oswald text-xl md:text-2xl font-bold uppercase tracking-tight mb-2 transition-colors duration-300 ${
                isExpanded ? 'text-accent' : 'text-slate-800'
              }`}
            >
              {service.title}
            </motion.h3>
            <motion.p layout className="font-manrope text-slate-600 text-sm md:text-base">
              {isExpanded ? service.fullDescription : service.shortDescription}
            </motion.p>
          </div>
        </motion.div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="mt-6"
            >
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {service.features.map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-2"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.05 + 0.1, type: 'spring' }}
                    >
                      <Check className="w-4 h-4 text-accent flex-shrink-0" />
                    </motion.div>
                    <span className="font-manrope text-sm text-slate-700">{feature}</span>
                  </motion.li>
                ))}
              </ul>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-accent hover:bg-accent-hover text-white font-manrope font-semibold rounded-sm btn-glow"
                data-testid={`service-cta-${service.id}`}
              >
                Discuss Requirements
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Hover/Tap indicator */}
      {!isExpanded && (
        <motion.div 
          className="absolute bottom-4 right-4"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="text-xs font-manrope uppercase tracking-wider text-accent">
            <span className="hidden md:inline">Hover for details</span>
            <span className="md:hidden">Tap for details</span>
          </span>
        </motion.div>
      )}
    </motion.div>
  );
};

const ServicesSection = () => {
  const [hoveredId, setHoveredId] = useState(null);
  const [tappedId, setTappedId] = useState(null);

  // Use tappedId on mobile, hoveredId on desktop
  const getExpandedId = () => tappedId || hoveredId;

  const handleTap = (id) => {
    // Toggle on tap for mobile
    setTappedId(tappedId === id ? null : id);
  };

  return (
    <section id="services" className="section-padding bg-slate-50 relative overflow-hidden" data-testid="services-section">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.span 
            className="font-manrope text-sm font-semibold text-accent uppercase tracking-widest mb-4 block"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Service Capabilities
          </motion.span>
          <h2 className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 uppercase tracking-tight mb-4">
            Integrated <span className="text-accent">FM Solutions</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            Comprehensive soft services designed for operational consistency and long-term value.
          </p>
        </motion.div>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              isExpanded={getExpandedId() === service.id}
              onHover={() => setHoveredId(service.id)}
              onLeave={() => setHoveredId(null)}
              onTap={() => handleTap(service.id)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
